import { useRafTimeout } from "ahooks";

import { useEffect, useMemo, useRef, useState } from "react";

import { ApiRoutesItem, mapField } from "@/api/routes";
import { searchPanoramaRefresh } from "@/api/routesFlights";
import { maxLastSeenTime } from "@/constants/config";

import useEvent from "./useEvent";

interface Props {
  isReady: boolean;
  data: ApiRoutesItem[];
  setData: (data: ApiRoutesItem[] | ((p: ApiRoutesItem[]) => ApiRoutesItem[])) => void;
  whiteList: string[];
}

/** 已经更新过的ID */
const updatedIdsCache = new Map<string, boolean>();

/** 最大并发数量 */
const maxConcurrency = 15;

/** 最大持续更新时间 */
const maxUpdateTime = 5 * 60 * 1000;

export const useRefreshPanorama = (props: Props) => {
  const { isReady, data, whiteList, setData } = props;

  const rafId = useRef<number>();

  const runCount = useRef<number>(0);

  const loopSwitch = useRef(true);

  const [refreshIds, setRefreshIds] = useState<string[]>([]);

  /** 请求中的ID队列 */
  const inProgressIdsQueue = useRef<string[]>([]);

  // console.log("%c [  ]-30", "font-size:13px; background:pink; color:#bf2c9f;", data);
  const getIds = useEvent(() => {
    const list = data
      .filter((item) => Date.now() / 1000 - item.points.ls > maxLastSeenTime)
      .map((item) => item.id)
      .filter((id) => !updatedIdsCache.has(id));

    // console.log(
    //   "%c [ 总数据 ]-26",
    //   "font-size:13px; background:pink; color:#bf2c9f;",
    //   data.length,
    //   list.length
    // );

    return Array.from(new Set(list));
  });

  const getNextConcurrencyIds = useEvent(() => {
    const ids = getIds();
    const appendCount = maxConcurrency - inProgressIdsQueue.current.length;
    return ids.slice(0, appendCount);
  });

  const startLoop = useEvent(async () => {
    const chunk = getNextConcurrencyIds();

    // console.log("%c [  ]-66", "font-size:13px; background:pink; color:#bf2c9f;", chunk);

    if (!chunk.length) {
      if (loopSwitch.current) {
        rafId.current = requestAnimationFrame(startLoop);
      }
      return;
    }

    /** 加入队列 */
    inProgressIdsQueue.current = inProgressIdsQueue.current.concat(chunk);
    setRefreshIds(inProgressIdsQueue.current);

    runCount.current++;

    // console.log(
    //   "%c [ 有效更新多少轮次 ]-55",
    //   "font-size:13px; background:pink; color:#bf2c9f;",
    //   runCount.current
    // );

    for (let index = 0; index < chunk.length; index++) {
      const id = chunk[index];

      /**
       * 请求过就算是更新过
       */
      updatedIdsCache.set(id, true);
      searchPanoramaRefresh({ search_key: id })
        .then((res) => {
          if (!loopSwitch.current) {
            return;
          }
          setData((prev) => {
            const sourceList = [...prev];
            /**
             * 更新成功后更新数据
             */

            if (res?.route_info?.length) {
              const routeInfo = res.route_info[0];

              const targetIndex = sourceList.findIndex((item) => item.id === routeInfo.id);

              // console.log(
              //   "%c [ 转换前的数据 ]-92",
              //   "font-size:13px; background:pink; color:#bf2c9f;",
              //   routeInfo
              // );

              /** 转换字段 */
              mapField(routeInfo, true);

              // console.log(
              //   "%c [ 转换后的数据 ]-105",
              //   "font-size:13px; background:pink; color:#bf2c9f;",
              //   routeInfo
              // );

              sourceList[targetIndex] = {
                ...routeInfo,
                supported_route: sourceList[targetIndex]?.supported_route,
              };

              // console.log(
              //   "%c [ 更新成功 ]-93",
              //   "font-size:13px; background:pink; color:#bf2c9f;",
              //   routeInfo
              // );
            }

            return sourceList;
          });
        })
        .finally(() => {
          /** 请求结束后从队列中移除 */
          inProgressIdsQueue.current = inProgressIdsQueue.current.filter((v) => v !== id);
          setRefreshIds(inProgressIdsQueue.current);

          if (loopSwitch.current) {
            rafId.current = requestAnimationFrame(startLoop);
          }
        });
    }
  });

  const stopLoop = useEvent(() => {
    if (rafId.current) {
      loopSwitch.current = false;
      cancelAnimationFrame(rafId.current);
    }
  });

  const excludeWhiteList = useEvent(() => {
    const whiteListIds = data
      .filter((item) => whiteList.includes(item.program.toLowerCase()))
      .map((item) => item.id);

    // console.log(
    //   "%c [  ]-163",
    //   "font-size:13px; background:pink; color:#bf2c9f;",
    //   whiteList,
    //   data,
    //   whiteListIds
    // );

    whiteListIds.forEach((id) => {
      updatedIdsCache.set(id, true);
    });
  });

  // useRafTimeout(() => {
  //   stopLoop();
  // }, maxUpdateTime);

  useEffect(() => {
    if (isReady) {
      excludeWhiteList();
      startLoop();
      // console.log(
      //   "%c [  ]-178",
      //   "font-size:13px; background:pink; color:#bf2c9f;",
      //   updatedIdsCache
      // );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  useEffect(() => {
    return () => {
      stopLoop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => {
    return { refreshIds };
  }, [refreshIds]);
};
